/**
 * Firebase v11+ Configuration & Service Initialization
 * Optimized for React 19+ with minimal re-renders and modern ES2022+ patterns
 * @module firebase
 */

import { initializeApp } from 'firebase/app'
import { connectAuthEmulator, getAuth } from 'firebase/auth'
import { connectFirestoreEmulator, getFirestore, serverTimestamp } from 'firebase/firestore'
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions'
import { getAnalytics } from 'firebase/analytics'
import { getPerformance } from 'firebase/performance'
import { firebaseConfig, isDevelopment, serviceUrls, useFirebaseEmulators } from './environment.js'

// Firebase app instance - singleton pattern
const app = initializeApp(firebaseConfig)

// Core Firebase services - exported as constants for optimal tree-shaking
export const auth = getAuth(app)
export const db = getFirestore(app)
export const functions = getFunctions(app)

// Firestore server timestamp utility - prevents client-side timestamp inconsistencies
export { serverTimestamp }

// Conditional Analytics & Performance - production only, lazy initialization
export const analytics = (!isDevelopment && !useFirebaseEmulators) 
  ? (() => {
      try { return getAnalytics(app) } 
      catch { return null }
    })()
  : null

export const performance = (!isDevelopment && !useFirebaseEmulators)
  ? (() => {
      try { return getPerformance(app) }
      catch { return null }
    })()
  : null

// Firebase emulator connections - development only, fail-safe pattern
if (useFirebaseEmulators) {
  const connectEmulator = (connectFn, ...args) => {
    try { connectFn(...args) } 
    catch (error) { 
      if (!error.message.includes('already')) {
        console.warn(`Emulator connection failed: ${error.message}`)
      }
    }
  }
  
  connectEmulator(connectAuthEmulator, auth, serviceUrls.emulators.auth, { disableWarnings: true })
  connectEmulator(connectFirestoreEmulator, db, serviceUrls.emulators.firestore.host, serviceUrls.emulators.firestore.port)
  connectEmulator(connectFunctionsEmulator, functions, serviceUrls.emulators.functions.host, serviceUrls.emulators.functions.port)
}

export default app
